import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import ReopenGuidesBtn from "@/components/ReopenGuidesBtn";
// iter v33 r07 — Auto-sync Rubrica CRM Pronto → Contact Provider iOS 18+
// (nessuna UI di attivazione: il prompt di sistema Apple gestisce il consenso)
import {
  ensureEnabled as ensureContactProvider,
  syncFromRubrica,
  getStatus as getContactProviderStatus,
  isProntoContactsAvailable,
} from "@/lib/prontoContacts";
import { SkeletonList } from "@/components/Skeleton";
import EmptyState from "@/components/EmptyState";
import PullIndicator from "@/components/PullIndicator";
import { usePullToRefresh } from "@/lib/usePullToRefresh";
import {
  Plus, MagnifyingGlass, Phone, WhatsappLogo, Trash,
  UploadSimple, AddressBook, X, Check, FloppyDisk, Spinner,
  PencilSimple, ArrowsClockwise, Sun, Snowflake, House,
  PhoneCall, Heart, XCircle, Megaphone, DotsThreeVertical,
} from "@phosphor-icons/react";
import BulkGreetingsModal from "@/components/BulkGreetingsModal";
import BroadcastModal from "@/components/BroadcastModal";
import { BULK_GREETINGS_LABEL } from "@/lib/labels";

const LISTING_TYPES = ["monolocale", "bilocale", "trilocale", "quadrilocale", "attico", "loft", "villa"];
const STATUSES = ["caldo", "tiepido", "freddo"];

// iter v28 r394 — User M1303: «caldo, freddo, tiepido non mi fanno
// impazzire. Freddo sembra morto.» Etichette ripensate come AZIONI
// concrete invece di temperature CRM-istiche:
//   caldo (heat ≥ 7)   → "Da chiamare"  (azione immediata, verde oliva)
//   tiepido (heat 4-6) → "Da risentire" (azione a giorni, ambra)
//   freddo (heat < 4)  → "Più avanti"   (lascia riposare, grigio neutro)
// iter v29 r214 — User M1504: «"in pausa" non so cosa voglia dire,
// "aspetta" non è chiaro». Etichetta finale: "Più avanti" (simmetrica
// alle altre: azione + tempo). Pill compatta mobile (solo dot, label
// solo da sm+).
// Manteniamo le chiavi backend ("caldo"/"tiepido"/"freddo") invariate
// per retro-compat con dati esistenti, cambia solo l'etichetta UI.
const STATUS_META = {
  caldo:   { Icon: PhoneCall,  label: "Da chiamare",  short: "Chiama",   sub: "Pronto · ora",            pill: "bg-[#E8F0DD] text-[#3D5A2C] border-[#7C8B6E]/35",  dot: "#5C6B4E" },
  tiepido: { Icon: Sun,        label: "Da risentire", short: "Risenti",  sub: "Sta valutando · giorni",  pill: "bg-amber-50 text-amber-700 border-amber-200",      dot: "#B45309" },
  freddo:  { Icon: Snowflake,  label: "Più avanti",   short: "Avanti",   sub: "Ricontattalo fra settimane", pill: "bg-slate-100 text-slate-600 border-slate-200",     dot: "#94A3B8" },
};

export default function Contatti() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editingId, setEditingId] = useState(null); // contact being edited; "new" = create form
  const [importing, setImporting] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPrefill, setBulkPrefill] = useState({});
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  // iter v28 r395 — User M1304: selezione multipla per eliminazione bulk.
  // Set<string> di contact.id selezionati. Vuoto = modalità normale.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // iter v33 r02 — Modalità selezione OFF di default: si attiva solo
  // dal kebab menu ("Seleziona per eliminare"). Rimuove il rumore
  // visivo della checkbox "Seleziona (N)" dalla vista principale.
  const [selectionMode, setSelectionMode] = useState(false);
  const fileInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Apri modal "Auguri di gruppo" se arriviamo con ?bulk=1 (link sidebar "WhatsApp a tutti")
  // O ?scheduled=<id> dall'email-promemoria delle pianificazioni.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("bulk") === "1") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBulkOpen(true);
      navigate("/dashboard/contatti", { replace: true });
      return;
    }
    const schedId = params.get("scheduled");
    if (schedId) {
      // iter v29 r044 — Carica la pianificazione e prefill modal
      (async () => {
        try {
          const r = await api.get(`/scheduled-greetings/${schedId}`);
          const sched = r?.data;
          if (sched) {
            setBulkPrefill({
              occasion: sched.occasion,
              customText: sched.custom_text || "",
              contactIds: Array.isArray(sched.contact_ids) ? sched.contact_ids : [],
              scheduledId: schedId,
              scheduledTitle: sched.title,
            });
            setBulkOpen(true);
            // Marca come "aperta" (per analytics + smetterla di mostrare la card "pronto da inviare")
            try { await api.post(`/scheduled-greetings/${schedId}/mark-opened`); } catch { /* best-effort */ }
          }
        } catch {
          // Pianificazione non trovata / scaduta / annullata. Silenzioso.
        }
        navigate("/dashboard/contatti", { replace: true });
      })();
    }
  }, [location.search, navigate]);

  useEffect(() => { load(); }, []);

  // Listener: l'editor segnala "apri questo contatto esistente" quando l'utente
  // sceglie di non duplicare e preferisce vedere il duplicato già presente.
  useEffect(() => {
    function handler(e) {
      const id = e?.detail?.id;
      if (id) setEditingId(id);
    }
    window.addEventListener("pronto:open-contact", handler);
    return () => window.removeEventListener("pronto:open-contact", handler);
  }, []);

  // iter v33 r07 — Stato del Contact Provider (iOS 18+ nativo)
  // Serve SOLO al piccolo badge "N contatti sincronizzati" quando attivo.
  // Nessuna UI di attivazione — il prompt Apple gestisce il primo consenso.
  const [cpStatus, setCpStatus] = useState({ supported: false, enabled: false, count: 0 });

  // iter r153 — Pulizia una tantum del flag debug rimasto in localStorage
  // da sessioni precedenti (visibile agli utenti come box nero in cima).
  useEffect(() => {
    try { localStorage.removeItem('pronto:cpDebug'); } catch { /* noop */ }
  }, []);

  async function refreshCpStatus() {
    try {
      const s = await getContactProviderStatus();
      setCpStatus(s);
    } catch { /* silent */ }
  }

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts");
      setContacts(data || []);

      // iter v33 r05 — Auto-sync Rubrica CRM → Contact Provider iOS 18+.
      // Chiamate silenziose (mai toast/errori). Al primo accesso su
      // iPhone iOS 18 il sistema mostrerà UN prompt "Pronto vuole
      // fornire contatti a questo iPhone": è il solo momento in cui
      // l'utente vede qualcosa. Da lì in poi tutto è invisibile.
      ensureContactProvider()
        .then(() => syncFromRubrica(data || []))
        .then(() => refreshCpStatus())
        .catch(() => { /* silent */ });
    } catch {
      toast.error("Caricamento contatti fallito");
    } finally {
      setLoading(false);
    }
  }

  // iter v28 r394 — User M1303: «forse li ordinerei per nome alfabetico».
  // iter v28 r411 — User M1320: ordinamento PER COGNOME (non più per nome).
  // Logica: prendi l'ultima parola del campo `name` come cognome. Se il
  // contatto ha una sola parola (es. "Mario"), usa quella come fallback.
  // Esempi: "Luca Ferrari" → "Ferrari", "Anna Maria Bruni" → "Bruni",
  // "Pietro" → "Pietro".
  const surnameOf = (fullName) => {
    const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0];
    return parts[parts.length - 1];
  };

  // Ordino dopo il filtro per evitare di riordinare anche durante la
  // ricerca (mantiene stabilità dei risultati). localeCompare 'it' fa il
  // sort corretto per accenti italiani (es. "Età" prima di "Ezio").
  const filtered = contacts
    .filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          (c.name || "").toLowerCase().includes(q) ||
          (c.phone || "").includes(search) ||
          (c.email || "").toLowerCase().includes(q) ||
          (c.preferences?.zones || []).some((z) => z.toLowerCase().includes(q))
        );
      }
      return true;
    })
    .sort((a, b) => {
      const sa = surnameOf(a.name);
      const sb = surnameOf(b.name);
      const cmp = sa.localeCompare(sb, "it", { sensitivity: "base" });
      if (cmp !== 0) return cmp;
      // Stessa lettera/cognome → ordina per nome completo
      return (a.name || "").localeCompare(b.name || "", "it", { sensitivity: "base" });
    });

  // iter v28 r411 — Lettere alfabetiche presenti per scroll laterale.
  // Calcoliamo le iniziali del COGNOME di ogni contatto filtrato per
  // costruire l'indice A-Z (come app Contatti di iOS).
  const alphaIndex = React.useMemo(() => {
    const present = new Set();
    filtered.forEach((c) => {
      const s = surnameOf(c.name);
      if (s) {
        const first = s[0].toUpperCase();
        if (/[A-Z]/.test(first)) present.add(first);
        else present.add("#"); // contatti che iniziano per cifra/altro
      }
    });
    // Restituiamo le 26 lettere + cancelletto. Quelle non presenti sono dim.
    const all = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").concat(["#"]);
    return all.map((l) => ({ letter: l, present: present.has(l) }));
  }, [filtered]);

  // iter v29 r262 — Raggruppamento per iniziale del cognome (iOS Contacts).
  // Le sezioni vengono renderizzate con header sticky "A", "B", "C"...
  const groupedByLetter = React.useMemo(() => {
    const groups = new Map();
    for (const c of filtered) {
      const s = surnameOf(c.name);
      const raw = s ? s[0].toUpperCase() : "#";
      const letter = /[A-Z]/.test(raw) ? raw : "#";
      if (!groups.has(letter)) groups.set(letter, []);
      groups.get(letter).push(c);
    }
    // Ordina: A-Z prima, "#" alla fine
    const ordered = Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === "#") return 1;
      if (b === "#") return -1;
      return a.localeCompare(b);
    });
    return ordered.map(([letter, items]) => ({ letter, items }));
  }, [filtered]);

  // Scroll alla prima riga il cui cognome inizia con la lettera tappata
  const scrollToLetter = (letter) => {
    const target = filtered.find((c) => {
      const s = surnameOf(c.name);
      if (!s) return false;
      const first = s[0].toUpperCase();
      if (letter === "#") return !/[A-Z]/.test(first);
      return first === letter;
    });
    if (!target) return;
    const el = document.querySelector(`[data-testid="contact-row-${target.id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  async function handleImportFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const fname = file.name.toLowerCase();
      // iter v28 r300 — auto-detect formato dal filename. Excel (xlsx/xls/xlsm)
      // → endpoint dedicato che fa auto-detect colonne (Nome/Cognome/Telefono/
      // Email/Note), niente mapping manuale.
      let url;
      if (fname.endsWith(".vcf")) url = "/contacts/import-vcard";
      else if (fname.endsWith(".xlsx") || fname.endsWith(".xls") || fname.endsWith(".xlsm")) url = "/contacts/import-excel";
      else url = "/contacts/import-csv";

      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });

      // Excel: messaggio più ricco con colonne rilevate
      if (url === "/contacts/import-excel" && data?.detected_columns) {
        const det = data.detected_columns;
        const found = Object.entries(det).filter(([, v]) => v).map(([k]) => k).join(", ");
        toast.success(
          `${data.inserted} contatti importati · ${data.skipped} saltati`,
          {
            description: data.had_header && found
              ? `Colonne rilevate: ${found}`
              : "Nessuna intestazione: ho riconosciuto i dati cella per cella.",
            duration: 6000,
          },
        );
      } else {
        toast.success(`${data.inserted} contatti importati · ${data.skipped} saltati`, { duration: 4000 });
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import fallito");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Eliminare questo contatto?")) return;
    try {
      await api.delete(`/contacts/${id}`);
      toast.success("Contatto eliminato");
      setContacts((cs) => cs.filter((c) => c.id !== id));
    } catch {
      toast.error("Eliminazione fallita");
    }
  }

  // iter v28 r395 — Selezione multipla + bulk delete.
  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleSelectAllVisible() {
    setSelectedIds((prev) => {
      const visibleIds = filtered.map((c) => c.id);
      const allSelected = visibleIds.every((id) => prev.has(id)) && visibleIds.length > 0;
      if (allSelected) {
        // deselect all visible
        const next = new Set(prev);
        for (const id of visibleIds) next.delete(id);
        return next;
      }
      // select all visible
      const next = new Set(prev);
      for (const id of visibleIds) next.add(id);
      return next;
    });
  }
  function clearSelection() {
    setSelectedIds(new Set());
  }
  async function handleBulkDelete() {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const msg = ids.length === 1
      ? "Eliminare 1 contatto?"
      : `Eliminare ${ids.length} contatti?`;
    if (!window.confirm(msg + " L'azione non è reversibile dalla UI.")) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/contacts/bulk-delete", { ids });
      toast.success(`${data?.deleted ?? ids.length} contatti eliminati`);
      setContacts((cs) => cs.filter((c) => !selectedIds.has(c.id)));
      clearSelection();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eliminazione massiva fallita");
    } finally {
      setBulkDeleting(false);
    }
  }

  // iter v29 r213 — Pull-to-refresh nativo iOS-feel
  const { pullDistance, isRefreshing } = usePullToRefresh(load);

  return (
    <AppShell title="Contatti" breadcrumb="Contatti">
      <PullIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />

      {/* iter v33 r02 — Header mobile-first: titolo + CTA primaria
          "+ Nuovo contatto" ben visibile · kebab per import/bulk/broadcast/
          selezione. Rimosso il FAB in basso che finiva dietro la bottom
          nav e non era chiaro. Rimossa la Dismissible Intro lunga (che
          occupava 2 righe di rumore visivo su mobile).
          Su schermi molto stretti (<380px) il testo del bottone si
          riduce automaticamente a "Nuovo" per non uscire dallo spazio. */}
      <header className="mb-3.5 flex items-center gap-2" data-testid="contatti-header">
        <div className="min-w-0 flex-1">
          <h1
            className="font-editorial text-[24px] sm:text-[28px] text-[#1C1C1A] tracking-tight leading-none"
            data-testid="contatti-title"
          >
            Rubrica
          </h1>
        </div>
        <button
          type="button"
          onClick={() => setEditingId("new")}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#5C6B4E] hover:bg-[#4A5A3E] active:scale-95 text-white font-semibold px-3.5 py-2 text-[13px] shadow-[0_2px_8px_rgba(92,107,78,0.28)] transition-all"
          data-testid="contacts-new-btn"
          aria-label="Nuovo contatto"
        >
          <Plus size={15} weight="bold" />
          <span>Nuovo contatto</span>
        </button>
        <ReopenGuidesBtn keys={["contatti:intro"]} testId="contatti-reopen-guides" />
        <input
          ref={fileInputRef}
          type="file"
          accept=".vcf,.csv,.xlsx,.xls,.xlsm"
          onChange={handleImportFile}
          className="hidden"
          data-testid="contacts-import-input"
        />
        <KebabMenu
          onImport={() => fileInputRef.current?.click()}
          onBulk={() => setBulkOpen(true)}
          onBroadcast={() => setBroadcastOpen(true)}
          onToggleSelection={() => {
            setSelectionMode((s) => {
              if (s) setSelectedIds(new Set());
              return !s;
            });
          }}
          selectionMode={selectionMode}
          disabled={contacts.length === 0}
          importing={importing}
        />
      </header>

      {/* iter v33 r07 — Contact Provider iOS 18: nessun banner "Attiva".
          Il primo lancio su iOS 18 chiama `ensureEnabled()` automaticamente
          → Apple mostra UN prompt sistema "Pronto vuole fornire contatti..."
          → tap Consenti → tutti i contatti Pronto sincronizzati.
          Se rifiuta il prompt: al prossimo restart app riproviamo.
          Manteniamo SOLO il badge discreto positivo quando è attivo. */}
      {isProntoContactsAvailable() && cpStatus.supported && cpStatus.enabled && cpStatus.count > 0 && (
        <div
          className="mb-3 flex items-center gap-2 text-[11.5px] text-[#5C6B4E]"
          data-testid="cp-banner-active"
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#5C6B4E]" />
          <span>
            {cpStatus.count} contatti sincronizzati su iPhone · visibili in WhatsApp
          </span>
        </div>
      )}

      {/* Search + filter compatti */}
      <div className="mb-4 space-y-2.5" data-testid="contacts-toolbar">
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute top-1/2 -translate-y-1/2 left-3.5 text-[#9A968B]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca nome, telefono, zona…"
            className="w-full pl-10 pr-3 py-2.5 text-[14px] bg-white border border-[#E8E5DE] rounded-full focus:outline-none focus:border-[#7C8B6E] placeholder:text-[#9A968B]"
            data-testid="contacts-search"
          />
        </div>
        {/* Segmented pill scrollabile · niente legenda verbose */}
        <div className="flex items-center gap-1.5 overflow-x-auto hide-scrollbar -mx-1 px-1 snap-x">
          <FilterPill
            active={!statusFilter}
            onClick={() => setStatusFilter("")}
            testId="contacts-filter-all"
          >
            Tutti
          </FilterPill>
          {STATUSES.map((s) => {
            const meta = STATUS_META[s];
            return (
              <FilterPill
                key={s}
                active={statusFilter === s}
                onClick={() => setStatusFilter(s)}
                testId={`contacts-filter-${s}`}
                dot={meta.dot}
              >
                {meta.label}
              </FilterPill>
            );
          })}
        </div>
      </div>

      {/* iter v28 r395 — Barra azioni bulk selezione.
          Visibile SOLO quando ci sono contatti selezionati. Permette:
          • toggle "Seleziona tutti i visibili"
          • bulk-delete con conferma
          • clear selection.
          Posizionata fra toolbar e lista per chiarezza visiva. */}
      {selectedIds.size > 0 && (
        <div
          className="mb-3 bg-[#1C1C1A] text-white rounded-lg px-3.5 py-2.5 flex items-center gap-3 flex-wrap"
          data-testid="contacts-bulk-bar"
        >
          <div className="text-[12.5px] font-semibold">
            {selectedIds.size} {selectedIds.size === 1 ? "selezionato" : "selezionati"}
          </div>
          <button
            type="button"
            onClick={toggleSelectAllVisible}
            className="text-[11.5px] underline underline-offset-2 hover:text-white/80 transition-colors"
            data-testid="contacts-select-all"
          >
            {filtered.length > 0 && filtered.every((c) => selectedIds.has(c.id))
              ? `Deseleziona tutti (${filtered.length})`
              : `Seleziona tutti i visibili (${filtered.length})`}
          </button>
          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={clearSelection}
              className="text-[11.5px] px-2.5 py-1.5 rounded-md hover:bg-white/10 transition-colors"
              data-testid="contacts-bulk-clear"
            >
              Annulla
            </button>
            {/* iter v28 r395 — User M1305: «manda WhatsApp ai selezionati».
                Apre BulkGreetingsModal con `prefillContactIds` pre-popolato
                dalla selezione corrente. L'utente sceglie l'occasione (auguri,
                annuncio nuovo, follow-up custom). */}
            <button
              type="button"
              onClick={() => setBulkOpen(true)}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md bg-[#25D366] hover:bg-[#1FB855] transition-colors"
              data-testid="contacts-bulk-whatsapp"
              title="Apri WhatsApp ai selezionati"
            >
              <WhatsappLogo size={12} weight="fill" />
              WhatsApp
            </button>
            <button
              type="button"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 disabled:opacity-60 transition-colors"
              data-testid="contacts-bulk-delete"
            >
              {bulkDeleting ? <Spinner size={11} className="animate-spin" /> : <Trash size={12} weight="fill" />}
              Elimina
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <SkeletonList count={6} />
      ) : filtered.length === 0 && !editingId ? (
        <EmptyState
          icon={AddressBook}
          title="Nessun contatto ancora"
          subtitle="Importa la rubrica del telefono (.vcf), un Excel/CSV, o aggiungi un cliente a mano."
          primaryCta={{ label: "Aggiungi a mano", icon: Plus, onClick: () => setEditingId("new"), testId: "contatti-empty-add" }}
          secondaryCta={{ label: "Importa CSV/VCF", icon: UploadSimple, onClick: () => fileInputRef.current?.click(), testId: "contatti-empty-import" }}
        />
      ) : (
        <div className="relative" data-testid="contacts-list-wrap">
          <div className="space-y-1.5" data-testid="contacts-list">
            {/* iter v33 r02 — Rimossa la riga "Seleziona (N)" sempre visibile.
                La modalità selezione multipla si attiva dal kebab menu
                ("Seleziona per eliminare") → mostra i checkbox sulle card. */}

            {/* iter v29 r262 — Section headers iOS-style raggruppati per
                iniziale di cognome. Sticky sotto AppShell topbar. */}
            {groupedByLetter.map(({ letter, items }) => (
              <React.Fragment key={letter}>
                <div
                  className="sticky top-[56px] sm:top-[64px] z-10 -mx-1 px-4 py-1 bg-[#F1F4ED]/95 backdrop-blur-md text-[11px] font-bold font-editorial text-[#7C8B6E] border-b border-[#E5E9E0]/60"
                  data-testid={`contacts-section-${letter}`}
                >
                  {letter}
                </div>
                <div className="space-y-1.5 pt-1 pb-1.5">
                  {items.map((c) => (
                    <ContactRow
                      key={c.id}
                      contact={c}
                      selectionMode={selectionMode}
                      selected={selectedIds.has(c.id)}
                      onToggleSelect={() => toggleSelect(c.id)}
                      onEdit={() => setEditingId(c.id)}
                      onDelete={() => handleDelete(c.id)}
                      onUpdated={load}
                    />
                  ))}
                </div>
              </React.Fragment>
            ))}
          </div>
          {/* iter v33 r04 — Indice A-Z laterale · RIABILITATO su mobile.
              Compatto (colonna 22px), font piccolo (9.5px) con tap-area 22×20.
              Supporta TAP + DRAG (scorri il dito sopra la colonna → scroll
              live, come Contatti iOS). Posizionato a right-0.5 con bg
              semi-trasparente per non coprire i pulsanti Chiama/WhatsApp
              delle card (che stanno a right-3 → distanza sufficiente). */}
          {filtered.length >= 10 && (
            <AlphaIndex
              letters={alphaIndex}
              onPick={scrollToLetter}
            />
          )}
        </div>
      )}

      {/* iter v33 r02 — FAB rimosso: sostituito dal bottone "+ Nuovo contatto"
          nell'header. Il FAB in basso destra finiva sopra la bottom nav
          e non era immediatamente comprensibile che aggiungeva un cliente. */}

      {editingId && (
        <ContactEditor
          contactId={editingId === "new" ? null : editingId}
          contact={editingId === "new" ? null : contacts.find((c) => c.id === editingId)}
          onClose={() => setEditingId(null)}
          onSaved={() => { setEditingId(null); load(); }}
        />
      )}
      <BulkGreetingsModal
        open={bulkOpen}
        onClose={() => { setBulkOpen(false); setBulkPrefill({}); }}
        prefillContactIds={
          bulkPrefill.contactIds && bulkPrefill.contactIds.length > 0
            ? bulkPrefill.contactIds
            : (selectedIds.size > 0 ? Array.from(selectedIds) : undefined)
        }
        prefillOccasion={bulkPrefill.occasion}
        prefillCustomText={bulkPrefill.customText}
      />
      <BroadcastModal open={broadcastOpen} onClose={() => setBroadcastOpen(false)} />
    </AppShell>
  );
}

function ContactRow({ contact, onEdit, onDelete, onUpdated, selected, onToggleSelect, selectionMode }) {
  const c = contact;
  const prefs = c.preferences || {};
  const prefSummary = [
    prefs.listing_types?.join("/"),
    prefs.zones?.join(", "),
    Number.isFinite(Number(prefs.budget_max)) && Number(prefs.budget_max) > 0
      ? `≤ ${(Number(prefs.budget_max) / 1000).toFixed(0)}k`
      : "",
  ].filter(Boolean).join(" · ");

  // iter 313 r11 — UX più chiara:
  //  • Stato è il dato PRIMARIO (caldo/tiepido/freddo con icona+sottotitolo)
  //  • Rimosso pill "heat N" che nessuno capiva (è dato interno)
  //  • Quick actions con icona+label e tooltip che spiega cosa fa OGNI azione
  //  • Mostra "Ultimo evento" (richiamato/interessato/visto casa…) se presente
  const statusKey = c.status || "tiepido";
  const meta = STATUS_META[statusKey] || STATUS_META.tiepido;
  const [busy, setBusy] = useState(false);
  // iter v28 r398 — User M1308: «se seleziono richiamato non me lo seleziona».
  // Mostra subito feedback visivo dell'ultima azione fatta su questo contatto.
  // Inizializzo da `c.last_quick_action` se presente (server-side), altrimenti
  // null. Quando l'utente clicca, aggiorno lo state locale.
  const [lastQuick, setLastQuick] = useState(c.last_quick_action || null);

  // iter v28 r398 — Quick actions con tooltip aggiornato alle nuove etichette
  // (vedi STATUS_META). Niente più "caldo/tiepido/freddo" nei tip.
  const QUICK = [
    { key: "richiamato",      label: "Richiamato",      Icon: PhoneCall, color: "bg-slate-50  text-slate-700  hover:bg-slate-100  border-slate-200",  activeColor: "bg-slate-700 text-white border-slate-700",       tip: "Lo segno come 'chiamato oggi'" },
    { key: "interessato",     label: "Interessato",     Icon: Heart,     color: "bg-amber-50  text-amber-700  hover:bg-amber-100  border-amber-200",  activeColor: "bg-amber-600 text-white border-amber-600",       tip: "Sposto su 'Da risentire' / 'Da chiamare'" },
    { key: "visto_casa",      label: "Visto casa",      Icon: House,     color: "bg-[#E8F0DD]  text-[#3D5A2C]  hover:bg-[#D9E5C8] border-[#7C8B6E]/35", activeColor: "bg-[#5C6B4E] text-white border-[#5C6B4E]",       tip: "Sposto su 'Da chiamare' (priorità alta)" },
    { key: "non_interessato", label: "Non interessato", Icon: XCircle,   color: "bg-slate-50  text-slate-600  hover:bg-slate-100  border-slate-200",  activeColor: "bg-slate-600 text-white border-slate-600",       tip: "Sposto su 'Più avanti'" },
  ];

  const onQuick = async (action, label) => {
    if (busy) return;
    setBusy(true);
    // iter v28 r398 — Optimistic update: aggiorno UI subito, prima della
    // risposta server. Se fallisce, rollback.
    const prev = lastQuick;
    setLastQuick(action);
    try {
      const { data } = await api.post(`/agenda/contacts/${c.id}/quick-action`, { action });
      const newStatusKey = data?.heat_after >= 7 ? "caldo" : data?.heat_after >= 4 ? "tiepido" : "freddo";
      const newLabel = STATUS_META[newStatusKey]?.label || newStatusKey;
      toast.success(`${label} · ora è "${newLabel}"`);
      onUpdated?.();
    } catch (e) {
      setLastQuick(prev);
      toast.error(e?.response?.data?.detail || "Operazione fallita");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`relative bg-white border rounded-xl px-3 py-2.5 transition-all ${
        selected
          ? "border-[#5C6B4E] bg-[#F8FAF5] ring-1 ring-[#5C6B4E]/30"
          : "border-[#E5E9E0] hover:border-[#7C8B6E]/40 hover:shadow-sm"
      }`}
      data-testid={`contact-row-${c.id}`}
    >
      {/* iter v33 r02 — Layout mobile-first più compatto (py-2.5 · px-3):
          ┌─────────────────────────────────────────────────┐
          │ [Avatar]  Mario Rossi                     [📞] │
          │           +39 333 1234567 · Brera, 500k   [💬] │
          │           ● Da chiamare                        │
          └─────────────────────────────────────────────────┘
          Checkbox visibile SOLO in selectionMode (attivata dal kebab).
          Tap sull'area centrale → apre Editor. */}
      <div className="flex items-start gap-3">
        {/* Checkbox selezione · SOLO in selectionMode (iter v33 r02) */}
        {selectionMode && onToggleSelect && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={onToggleSelect}
            className="shrink-0 w-4 h-4 mt-2 accent-[#5C6B4E] cursor-pointer"
            aria-label={`Seleziona ${c.name}`}
            data-testid={`contact-select-${c.id}`}
            onClick={(e) => e.stopPropagation()}
          />
        )}

        {/* Avatar iniziali · cerchio sage chiaro */}
        <ContactAvatar name={c.name} />

        {/* Nome + sub-line + status · tap → Editor */}
        <button
          type="button"
          onClick={onEdit}
          className="flex-1 min-w-0 text-left -my-1 py-1 group/tap"
          data-testid={`contact-tap-${c.id}`}
          aria-label={`Apri ${c.name}`}
        >
          {/* Nome cognome a capo, niente truncate */}
          {(() => {
            const _fullName = (c.name || "").trim();
            const _parts = _fullName.split(/\s+/).filter(Boolean);
            if (_parts.length <= 1) {
              return (
                <div
                  className="font-bold text-[15px] text-[#1A1D1A] leading-tight break-words"
                  data-testid={`contact-name-${c.id}`}
                >
                  {_fullName || "Senza nome"}
                </div>
              );
            }
            const _surname = _parts[_parts.length - 1];
            const _given = _parts.slice(0, -1).join(" ");
            return (
              <div
                className="text-[15px] text-[#1A1D1A] leading-tight break-words"
                data-testid={`contact-name-${c.id}`}
              >
                <span className="font-medium">{_given}</span>{" "}
                <span className="font-bold">{_surname}</span>
              </div>
            );
          })()}

          {/* Sub-line · telefono · zona · budget */}
          {(c.phone || prefSummary) && (
            <div className="text-[12px] text-[#5B6358] leading-snug mt-0.5 line-clamp-1">
              {c.phone && <span>{c.phone}</span>}
              {c.phone && prefSummary && <span className="mx-1.5 text-[#B3B0A8]">·</span>}
              {prefSummary && <span>{prefSummary}</span>}
            </div>
          )}

          {/* Status dot + label · inline, leggero */}
          <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#5B6358]" data-testid={`contact-status-${c.id}`}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.dot }} />
            <span>{meta.label}</span>
            {lastQuick && (
              <>
                <span className="text-[#D6D3CC]">·</span>
                <span className="inline-flex items-center gap-0.5 text-[10.5px] font-medium text-[#7C8B6E]">
                  <Check size={9} weight="bold" />
                  {QUICK.find((q) => q.key === lastQuick)?.label || lastQuick}
                </span>
              </>
            )}
          </div>
        </button>

        {/* iter v33 r02 — Azioni rapide affiancate in verticale, più compatte.
            Se il contatto non ha telefono → pulsante chiaro "Aggiungi numero"
            (era "+ contatti" ambiguo). */}
        <div className="flex flex-col gap-1.5 shrink-0">
          {c.phone ? (
            <>
              <a
                href={`tel:${c.phone.replace(/\s/g, "")}`}
                className="w-9 h-9 rounded-full bg-[#5C6B4E] hover:bg-[#4A5A3E] text-white flex items-center justify-center transition-colors active:scale-95"
                data-testid={`contact-call-${c.id}`}
                aria-label={`Chiama ${c.name}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Phone size={15} weight="fill" />
              </a>
              <a
                href={`https://wa.me/${c.phone.replace(/^\+/, "").replace(/\s/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="w-9 h-9 rounded-full bg-[#25D366] hover:bg-[#1FB855] text-white flex items-center justify-center transition-colors active:scale-95"
                data-testid={`contact-wa-${c.id}`}
                aria-label={`WhatsApp ${c.name}`}
                onClick={(e) => e.stopPropagation()}
              >
                <WhatsappLogo size={15} weight="fill" />
              </a>
            </>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
              className="inline-flex items-center gap-1 rounded-full bg-[#F1F4ED] text-[#5C6B4E] hover:bg-[#E5E9E0] border border-[#D6DBCC] font-semibold text-[11px] px-2.5 py-1.5 leading-none transition-colors"
              data-testid={`contact-add-phone-${c.id}`}
              aria-label={`Aggiungi numero a ${c.name}`}
            >
              <Plus size={11} weight="bold" />
              Aggiungi numero
            </button>
          )}
        </div>
      </div>

      {/* iter v29 r262 — Quick actions stato collassate, opt-in con
          tap su "Aggiorna" · click sulla card va direttamente in Edit.
          Mantenuto per chi vuole loggare un evento veloce senza aprire
          l'editor completo. */}
      <details className="mt-2 group/details">
        <summary
          className="cursor-pointer text-[10.5px] font-medium text-[#9A968B] hover:text-[#5C6B4E] inline-flex items-center gap-1 list-none select-none"
          data-testid={`contact-quick-toggle-${c.id}`}
        >
          <span className="transition-transform group-open/details:rotate-90 text-[8px]">▸</span>
          <span>Aggiorna stato</span>
        </summary>
        <div className="flex items-center gap-1.5 flex-wrap mt-2" data-testid={`contact-quick-${c.id}`}>
          {QUICK.map((q) => {
            const QIcon = q.Icon;
            const isActive = lastQuick === q.key;
            return (
              <button
                key={q.key}
                onClick={() => onQuick(q.key, q.label)}
                disabled={busy}
                className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border font-medium transition-colors disabled:opacity-50 ${isActive ? q.activeColor : q.color}`}
                title={q.tip}
                data-testid={`contact-quick-${q.key}-${c.id}`}
                aria-pressed={isActive}
              >
                {isActive ? <Check size={10} weight="bold" /> : <QIcon size={10} weight="fill" />}
                {q.label}
              </button>
            );
          })}
        </div>
      </details>
    </div>
  );
}

export function ContactEditor({ contactId, contact, onClose, onSaved }) {
  const isNew = !contactId;
  const [form, setForm] = useState(() => ({
    name: contact?.name || "",
    phone: contact?.phone || "",
    email: contact?.email || "",
    notes: contact?.notes || "",
    birthdate: contact?.birthdate || "",
    // iter v28 r400 — campo opzionale "sesso" per salutation più affidabile
    // su nomi unisex (Andrea, Sasha) e per filtro nel modal WhatsApp.
    gender: contact?.gender || "",
    status: contact?.status || "tiepido",
    consent_marketing: contact?.consent_marketing || false,
    preferences: {
      listing_types: contact?.preferences?.listing_types || [],
      zones: contact?.preferences?.zones || [],
      budget_min: contact?.preferences?.budget_min ?? "",
      budget_max: contact?.preferences?.budget_max ?? "",
      surface_min: contact?.preferences?.surface_min ?? "",
      surface_max: contact?.preferences?.surface_max ?? "",
      must_have: contact?.preferences?.must_have || [],
      must_avoid: contact?.preferences?.must_avoid || [],
    },
  }));
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPref = (k, v) => setForm((f) => ({ ...f, preferences: { ...f.preferences, [k]: v } }));

  const toggleType = (t) => {
    const next = form.preferences.listing_types.includes(t)
      ? form.preferences.listing_types.filter((x) => x !== t)
      : [...form.preferences.listing_types, t];
    setPref("listing_types", next);
  };

  async function save({ force = false } = {}) {
    const nameTrim = form.name.trim();
    if (!nameTrim) {
      toast.error("Nome obbligatorio");
      return;
    }
    // Strict: require first + last name (mirrors backend validator).
    const cleaned = nameTrim.replace(
      /^(sig\.?ra?|signora?|signor[ae]?|dott\.?(essa)?|avv\.?|geom\.?|ing\.?|arch\.?|prof\.?(essoressa)?)\s+/i,
      "",
    );
    const tokens = cleaned.split(/\s+/).filter((t) => /[a-zA-ZÀ-ÿ]/.test(t));
    if (tokens.length < 2 || tokens.some((t, i) => i < 2 && t.length < 2)) {
      toast.error("Inserisci nome e cognome (es. Luca Ferrari)");
      return;
    }
    setSaving(true);
    const cleanString = (s) => {
      const v = (s || "").toString().trim();
      return v === "" ? null : v;
    };
    const payload = {
      name: form.name.trim(),
      phone: cleanString(form.phone),
      email: cleanString(form.email),
      notes: cleanString(form.notes),
      birthdate: cleanString(form.birthdate),
      gender: form.gender || null,
      status: form.status,
      consent_marketing: form.consent_marketing,
      preferences: {
        listing_types: form.preferences.listing_types,
        zones: form.preferences.zones.filter(Boolean),
        budget_min: form.preferences.budget_min === "" ? null : Number(form.preferences.budget_min),
        budget_max: form.preferences.budget_max === "" ? null : Number(form.preferences.budget_max),
        surface_min: form.preferences.surface_min === "" ? null : Number(form.preferences.surface_min),
        surface_max: form.preferences.surface_max === "" ? null : Number(form.preferences.surface_max),
        must_have: form.preferences.must_have,
        must_avoid: form.preferences.must_avoid,
      },
    };
    const qs = force ? "?force=1" : "";
    try {
      if (isNew) await api.post(`/contacts${qs}`, payload);
      else await api.patch(`/contacts/${contactId}${qs}`, payload);
      toast.success(isNew ? "Contatto creato" : "Contatto aggiornato");
      onSaved();
    } catch (err) {
      // 409 dedup → mostra dialog di conferma con dettagli del contatto esistente
      if (err?.response?.status === 409 && err?.response?.data?.detail?.code === "contact_duplicate") {
        const existing = err.response.data.detail.existing || {};
        const meta = [existing.phone, existing.email].filter(Boolean).join(" · ");
        const confirmMsg =
          `Esiste già un contatto chiamato "${existing.name}"` +
          (meta ? `\n(${meta})` : "") +
          `.\n\nVuoi crearne uno NUOVO comunque?\n(Premi Annulla per aprire quello esistente)`;
        if (window.confirm(confirmMsg)) {
          setSaving(false);
          return save({ force: true });
        } else {
          // L'utente vuole aprire il duplicato esistente
          onClose?.();
          if (existing.id) {
            // Apre l'editor dell'esistente
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent("pronto:open-contact", { detail: { id: existing.id } }));
            }, 50);
          }
        }
      } else {
        const detail = err?.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : detail?.message || "Salvataggio fallito";
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[20px] font-semibold text-[#1C1C1A]">{isNew ? "Nuovo contatto" : "Modifica contatto"}</h2>
          <button onClick={onClose} className="text-[#6B6A65] hover:text-[#1C1C1A] p-1"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <Field label="Nome e cognome*" v={form.name} on={(v) => setField("name", v)} placeholder="Es. Luca Ferrari" testid="ce-name" />
          <Field label="Telefono" v={form.phone} on={(v) => setField("phone", v)} placeholder="+39 333..." testid="ce-phone" />
          <Field label="Email" v={form.email} on={(v) => setField("email", v)} testid="ce-email" />
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-[#6B6A65]">Data di nascita</label>
            <input
              type="date"
              value={form.birthdate || ""}
              onChange={(e) => setField("birthdate", e.target.value || null)}
              className="w-full mt-1 px-2.5 py-2 text-[13px] bg-white border border-[#E8E5DE] rounded-md focus:outline-none focus:border-[#7C8B6E]"
              data-testid="ce-birthdate"
            />
            <div className="text-[10px] text-[#9A968B] mt-0.5">Per gli auguri di compleanno automatici</div>
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-[#6B6A65]">Sesso</label>
            <div className="flex items-center gap-1 mt-1 bg-[#F5F4F0] rounded-md p-0.5" data-testid="ce-gender">
              {[
                { v: "", l: "Non specificato" },
                { v: "m", l: "Uomo" },
                { v: "f", l: "Donna" },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setField("gender", o.v)}
                  className={`flex-1 text-[12px] px-2 py-1.5 rounded transition-colors ${
                    form.gender === o.v ? "bg-white shadow-sm text-[#1C1C1A] font-semibold" : "text-[#6B6A65]"
                  }`}
                  data-testid={`ce-gender-${o.v || "none"}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-[#9A968B] mt-0.5">Aiuta con &quot;Sig./Sig.ra&quot; per nomi unisex</div>
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-[#6B6A65]">Stato</label>
            <select value={form.status} onChange={(e) => setField("status", e.target.value)} className="w-full mt-1 px-2.5 py-2 text-[13px] bg-white border border-[#E8E5DE] rounded-md" data-testid="ce-status">
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>)}
            </select>
          </div>
        </div>

        <div className="my-4 pt-4 border-t border-[#EAE7DF]">
          <h3 className="text-[12px] uppercase tracking-[0.14em] font-bold text-[#7C8B6E] mb-1">Cosa cerca</h3>
          <p className="text-[11.5px] text-[#9A968B] mb-3 leading-snug">
            Imposta tipologia, zona e budget per usare <strong>&quot;Notifica clienti interessati&quot;</strong>{" "}
            sugli annunci: Pronto trova chi cerca proprio quell&apos;immobile e ti propone di mandargli
            il link in WhatsApp.
          </p>
          <div className="mb-3">
            <label className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-[#6B6A65]">Tipologia</label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {LISTING_TYPES.map((t) => (
                <button
                  key={t}
                  onClick={() => toggleType(t)}
                  className={`text-[11.5px] px-2.5 py-1 rounded-full capitalize border transition-colors ${
                    form.preferences.listing_types.includes(t)
                      ? "bg-[#7C8B6E] text-white border-[#7C8B6E]"
                      : "bg-white text-[#6B6A65] border-[#E8E5DE] hover:border-[#7C8B6E]"
                  }`}
                  data-testid={`ce-type-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <Field
            label="Zone (separa con virgole)"
            v={form.preferences.zones.join(", ")}
            on={(v) => setPref("zones", v.split(",").map((z) => z.trim()).filter(Boolean))}
            placeholder="Brera, Porta Romana"
            testid="ce-zones"
          />
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Field label="Budget min (€)" v={form.preferences.budget_min} on={(v) => setPref("budget_min", v)} type="number" testid="ce-bmin" />
            <Field label="Budget max (€)" v={form.preferences.budget_max} on={(v) => setPref("budget_max", v)} type="number" testid="ce-bmax" />
            <Field label="Mq min" v={form.preferences.surface_min} on={(v) => setPref("surface_min", v)} type="number" testid="ce-smin" />
            <Field label="Mq max" v={form.preferences.surface_max} on={(v) => setPref("surface_max", v)} type="number" testid="ce-smax" />
          </div>
        </div>

        <div className="mb-4">
          <label className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-[#6B6A65]">Note</label>
          <textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={2} className="w-full mt-1 px-2.5 py-2 text-[13px] bg-white border border-[#E8E5DE] rounded-md" placeholder="Note libere..." />
        </div>

        <label className="flex items-center gap-2 text-[12px] text-[#6B6A65] mb-5 cursor-pointer">
          <input type="checkbox" checked={form.consent_marketing} onChange={(e) => setField("consent_marketing", e.target.checked)} className="rounded" data-testid="ce-consent" />
          <span>Consenso marketing GDPR (può ricevere proposte WhatsApp)</span>
        </label>

        <div className="flex items-center justify-between gap-2 pt-4 border-t border-[#EAE7DF]">
          {/* iter v29 r262 — Pulsante Elimina dentro l'editor (dopo redesign
              card: cestino rimosso dalla card per recuperare spazio). */}
          {!isNew && (
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm("Eliminare questo contatto? L'azione non è reversibile.")) return;
                try {
                  await api.delete(`/contacts/${contactId}`);
                  toast.success("Contatto eliminato");
                  onSaved();
                } catch {
                  toast.error("Eliminazione fallita");
                }
              }}
              className="text-[12.5px] text-red-600 hover:underline font-medium"
              data-testid="ce-delete"
            >
              Elimina contatto
            </button>
          )}
          <div className={`flex items-center gap-2 ${isNew ? "ml-auto" : ""}`}>
            <button onClick={onClose} className="text-[12.5px] px-4 py-2 text-[#6B6A65] hover:text-[#1C1C1A]">Annulla</button>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-4 py-2 rounded-md bg-[#1C1C1A] hover:bg-black text-white disabled:opacity-50"
              data-testid="ce-save"
            >
              {saving ? <Spinner size={13} className="animate-spin" /> : <FloppyDisk size={13} weight="bold" />}
              Salva
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, on, placeholder, type = "text", testid }) {
  return (
    <div>
      <label className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-[#6B6A65]">{label}</label>
      <input
        type={type}
        value={v ?? ""}
        onChange={(e) => on(e.target.value)}
        placeholder={placeholder}
        className="w-full mt-1 px-2.5 py-2 text-[13px] bg-white border border-[#E8E5DE] rounded-md focus:outline-none focus:border-[#7C8B6E]"
        data-testid={testid}
      />
    </div>
  );
}


// iter v29 r262 — Filter pill segmentato (Tutti, Da chiamare, ecc).
// Compatto, mobile-first, supporta dot colorato per gli stati.
function FilterPill({ active, onClick, children, dot, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 text-[12.5px] px-3.5 py-1.5 rounded-full whitespace-nowrap snap-start font-medium border transition-colors ${
        active
          ? "bg-[#7C8B6E] text-white border-[#7C8B6E]"
          : "bg-white text-[#5B6358] border-[#E8E5DE] hover:border-[#7C8B6E]"
      }`}
    >
      {dot && (
        <span
          className="w-1.5 h-1.5 rounded-full shrink-0"
          style={{ background: active ? "#FFFFFF" : dot }}
        />
      )}
      {children}
    </button>
  );
}

// iter v29 r262 — Kebab menu compatto (3 dots) per azioni secondarie
// dell'header: Importa, WhatsApp di gruppo, Broadcast.
// Sostituisce 3 bottoni inline che facevano overflow su mobile.
function KebabMenu({ onImport, onBulk, onBroadcast, onToggleSelection, selectionMode, disabled, importing }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-9 h-9 rounded-full bg-white border border-[#E8E5DE] hover:border-[#7C8B6E] text-[#1C1C1A] flex items-center justify-center transition-colors"
        data-testid="contacts-kebab-btn"
        aria-label="Altre azioni"
        aria-expanded={open}
      >
        <DotsThreeVertical size={18} weight="bold" />
      </button>
      {open && (
        <div
          className="absolute top-full right-0 mt-1.5 min-w-[220px] bg-white border border-[#E5E9E0] rounded-lg shadow-xl z-50 overflow-hidden"
          data-testid="contacts-kebab-menu"
        >
          <button
            type="button"
            onClick={() => { setOpen(false); onImport?.(); }}
            disabled={importing}
            className="w-full text-left px-4 py-3 text-[13.5px] text-[#1C1C1A] hover:bg-[#F1F4ED] flex items-center gap-2.5 disabled:opacity-50"
            data-testid="contacts-import-btn"
          >
            {importing ? (
              <Spinner size={15} className="animate-spin text-[#7C8B6E]" />
            ) : (
              <UploadSimple size={15} weight="regular" className="text-[#7C8B6E]" />
            )}
            <span>Importa <span className="text-[#9A968B] text-[11.5px]">(Excel · CSV · vCard)</span></span>
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); onBulk?.(); }}
            disabled={disabled}
            className="w-full text-left px-4 py-3 text-[13.5px] text-[#1C1C1A] hover:bg-[#F1F4ED] flex items-center gap-2.5 disabled:opacity-40"
            data-testid="contacts-bulk-greetings-btn"
          >
            <WhatsappLogo size={15} weight="regular" className="text-[#25D366]" />
            <span>{BULK_GREETINGS_LABEL}</span>
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); onBroadcast?.(); }}
            disabled={disabled}
            className="w-full text-left px-4 py-3 text-[13.5px] text-[#1C1C1A] hover:bg-[#F1F4ED] flex items-center gap-2.5 disabled:opacity-40"
            data-testid="contacts-broadcast-btn"
          >
            <Megaphone size={15} weight="regular" className="text-[#7C8B6E]" />
            <span>Broadcast WhatsApp</span>
          </button>
          {/* iter v33 r02 — Selezione multipla spostata nel kebab.
              Rimuove la checkbox sempre visibile "Seleziona (N)" dal
              listato principale, aggiungendola qui come modalità dedicata. */}
          <div className="border-t border-[#EFECE4]" />
          <button
            type="button"
            onClick={() => { setOpen(false); onToggleSelection?.(); }}
            disabled={disabled}
            className="w-full text-left px-4 py-3 text-[13.5px] text-[#1C1C1A] hover:bg-[#F1F4ED] flex items-center gap-2.5 disabled:opacity-40"
            data-testid="contacts-toggle-selection-btn"
          >
            <Check size={15} weight="regular" className="text-[#7C8B6E]" />
            <span>{selectionMode ? "Esci dalla selezione" : "Seleziona per eliminare"}</span>
          </button>
        </div>
      )}
    </div>
  );
}


// iter v29 r262 — Avatar circolare con iniziali (iOS Contacts feel).
// Hash deterministico del nome → tinta sage sfumata, leggibile.
function ContactAvatar({ name }) {
  const trimmed = (name || "").trim();
  const parts = trimmed.split(/\s+/).filter(Boolean);
  const initials = parts.length === 0
    ? "?"
    : parts.length === 1
      ? parts[0].slice(0, 2).toUpperCase()
      : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  // Tinta deterministica dalla somma char-codes · 4 varianti sage
  const palette = [
    { bg: "#E5E9E0", fg: "#5C6B4E" },
    { bg: "#EAEFEE", fg: "#5C7C8A" },
    { bg: "#F5F0E6", fg: "#8A6E3D" },
    { bg: "#F1F4ED", fg: "#7C8B6E" },
  ];
  const hash = trimmed.split("").reduce((a, ch) => a + ch.charCodeAt(0), 0);
  const c = palette[hash % palette.length];
  return (
    <div
      className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center font-semibold text-[14px] tracking-wide"
      style={{ background: c.bg, color: c.fg }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

// iter v33 r04 — Indice A-Z laterale con TAP + DRAG (iOS Contacts style).
// Colonna sottile (22px) fissata a destra dello schermo. Tap su una
// lettera → scrolla lì. Drag del dito sulla colonna → scroll live
// (calcola quale lettera è sotto il touch e chiama onPick). Le lettere
// non presenti sono dim e non cliccabili.
function AlphaIndex({ letters, onPick }) {
  const containerRef = useRef(null);
  const lastPicked = useRef(null);

  function letterAtY(clientY) {
    const el = containerRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const y = clientY - rect.top;
    if (y < 0 || y > rect.height) return null;
    const idx = Math.floor((y / rect.height) * letters.length);
    const clamped = Math.max(0, Math.min(letters.length - 1, idx));
    return letters[clamped];
  }

  function handleTouch(clientY) {
    const item = letterAtY(clientY);
    if (!item || !item.present) return;
    if (lastPicked.current === item.letter) return; // debounce → una sola scroll per lettera
    lastPicked.current = item.letter;
    onPick(item.letter);
    // feedback haptic leggero se supportato
    try {
      if (navigator.vibrate) navigator.vibrate(6);
    } catch { /* ignore */ }
  }

  return (
    <div
      ref={containerRef}
      className="fixed right-0.5 top-1/2 -translate-y-1/2 flex flex-col items-center justify-center bg-white/70 backdrop-blur-sm rounded-full py-2 z-30 select-none touch-none"
      data-testid="contacts-alpha-index"
      onTouchStart={(e) => { lastPicked.current = null; handleTouch(e.touches[0].clientY); }}
      onTouchMove={(e) => { e.preventDefault(); handleTouch(e.touches[0].clientY); }}
      onTouchEnd={() => { lastPicked.current = null; }}
      onMouseDown={(e) => { lastPicked.current = null; handleTouch(e.clientY); }}
    >
      {letters.map(({ letter, present }) => (
        <button
          key={letter}
          type="button"
          tabIndex={-1}
          onClick={() => present && onPick(letter)}
          disabled={!present}
          className={`block w-[22px] leading-[1] text-[9.5px] font-bold text-center py-[1.5px] pointer-events-none ${
            present ? "text-[#5C6B4E]" : "text-[#CFD3C7]"
          }`}
          aria-label={`Salta a ${letter}`}
          data-testid={`contacts-alpha-${letter}`}
        >
          {letter}
        </button>
      ))}
    </div>
  );
}

